/**
 * useFormCache
 *
 * Persist a form state object to localStorage keyed by `cacheKey`, returning
 * [state, setState, clearCache]. Merges cached shape onto initial state.
 */
import { useState, useEffect } from 'react';

const CACHE_KEY = 'campaign_form_cache';

export function useFormCache(initialState, cacheKey = CACHE_KEY) {
  const storageKey = cacheKey || CACHE_KEY;
  
  // Try to load cached data first
  const [state, setState] = useState(() => {
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        // Validate the cache has the expected shape
        if (typeof parsed === 'object' && parsed !== null) {
          return {
            ...initialState,
            ...parsed
          };
        }
      }
    } catch (err) {
      console.warn('Failed to load form cache:', err);
    }
    return initialState;
  });

  // Save to cache whenever state changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (err) {
      console.warn('Failed to save form cache:', err);
    }
  }, [state, storageKey]);

  // Clear cache when form is submitted successfully
  const clearCache = () => {
    try {
      // Remove from localStorage
      localStorage.removeItem(storageKey);
      console.log(`Form cache cleared for key: ${storageKey}`);
      
      // Reset state to initial state
      setState(initialState);
    } catch (err) {
      console.warn('Failed to clear form cache:', err);
    }
  };

  return [state, setState, clearCache];
}